import { useState } from 'react';
import { TabPanel, Tabs } from '@/ui/Tabs';
import { Aprendizaje } from './Aprendizaje';
import { Bienestar } from './Bienestar';
import { Cultura } from './Cultura';
import { General } from './General';
import type { Pestana } from './api';

/**
 * Analítica — `#analitica`, y `#analitica/<pestaña>`. Módulo 14, opción 2.
 *
 * Cuatro pestañas: el tablero de Bienestar, el resumen general, la analítica de
 * aprendizaje y la de cultura. Solo `analitica:manage`.
 *
 * ## Lo que cambia
 *
 * - **Cada pestaña tiene su dirección.** El vanilla las guardaba solo en
 *   memoria: recargar volvía siempre a Bienestar y no se podía enlazar
 *   «Analítica → Cultura» a nadie.
 * - **Volver a una pestaña ya vista no vuelve a preguntar** mientras no se
 *   recargue: el vanilla repetía las peticiones en cada clic.
 * - Las pestañas son las del sistema: con flechas, `aria-selected` y foco. Las
 *   del vanilla eran botones sueltos con la clase `active`.
 * - Los dos gráficos, las alertas y los iconos de los valores cuentan lo suyo
 *   en `piezas.tsx` y en `Cultura.tsx`.
 *
 * ## Por qué la dirección se escribe con `replaceState`
 *
 * El router del shell vuelve a montar la capa React en cada `hashchange`, y con
 * ella nace un cliente de caché nuevo: cambiar de pestaña por el hash costaría
 * volver a pedirlo todo, que es justo lo que se quería quitar. `replaceState`
 * deja la dirección puesta —se puede copiar, recargar y compartir— sin
 * remontar. Lo que no hace es dejar rastro en el historial: el Atrás sale de
 * Analítica, como antes.
 */

const PESTANAS = [
  { clave: 'bienestar', etiqueta: 'Tablero Bienestar', icono: 'bi-heart-pulse' },
  { clave: 'general', etiqueta: 'General', icono: 'bi-grid-1x2' },
  { clave: 'aprendizaje', etiqueta: 'Aprendizaje', icono: 'bi-mortarboard' },
  { clave: 'cultura', etiqueta: 'Cultura', icono: 'bi-people' },
] as const;

const CLAVES = PESTANAS.map((p) => p.clave);

function pestanaDeLaRuta(): Pestana {
  const sub = String(window._routeParams?.[0] ?? '');
  return (CLAVES as readonly string[]).includes(sub) ? (sub as Pestana) : 'bienestar';
}

export function Analitica() {
  const [pestana, setPestana] = useState<Pestana>(pestanaDeLaRuta);

  function cambiar(clave: string) {
    const nueva = (CLAVES as readonly string[]).includes(clave) ? (clave as Pestana) : 'bienestar';
    setPestana(nueva);
    // La dirección se queda apuntando a la pestaña abierta, sin remontar.
    history.replaceState(null, '', `#analitica/${nueva}`);
  }

  return (
    <>
      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
          <i className="bi bi-graph-up-arrow" aria-hidden="true" /> Analítica
        </h1>
        <p className="mt-1 text-3 text-muted">
          Indicadores del portal, y las alertas de lo que conviene mirar.
        </p>
      </header>

      <Tabs valor={pestana} onCambio={cambiar} pestanas={[...PESTANAS]}>
        <TabPanel clave="bienestar"><Bienestar activa={pestana === 'bienestar'} /></TabPanel>
        <TabPanel clave="general"><General activa={pestana === 'general'} /></TabPanel>
        <TabPanel clave="aprendizaje"><Aprendizaje activa={pestana === 'aprendizaje'} /></TabPanel>
        <TabPanel clave="cultura"><Cultura activa={pestana === 'cultura'} /></TabPanel>
      </Tabs>
    </>
  );
}
